import React, { forwardRef, memo } from "react";
import { Grid, Typography, Paper } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  yn,
  fmtText,
  fmtNum,
  fmtDateString,
  fmtTimestamp,
  fmtTime,
  fmtGps,
  finalDtw,
  INSPECTION_CHECKLIST,
  setbackRows,
  buildWqGrid,
  wqMetaSummary,
  reportTitle,
  BLANK_WQ_PARAMETERS,
  BLANK_WQ_READING_COLUMNS,
  BLANK_WL_ROWS,
  BLANK_METER_ROWS,
} from "./snapshotFormat";

const useStyles = makeStyles({
  container: {
    pageBreakAfter: "always",
    position: "relative",
    margin: "16px",
    padding: "20px",
    backgroundColor: "#fff",
    fontFamily: "Times New Roman",
    // Print the shaded section bands / table headers even when the browser's
    // "background graphics" print option is off.
    "-webkit-print-color-adjust": "exact",
    printColorAdjust: "exact",
  },
  headerImage: {
    height: "80px",
  },
  headerTitle: {
    fontSize: "14px",
    fontWeight: "bold",
  },
  smallText: {
    fontSize: "10px",
    fontWeight: "bold",
  },
  mainTitle: {
    fontSize: "17px",
    fontWeight: "bold",
    textAlign: "center",
    margin: "14px 0",
  },
  sectionTitle: {
    fontSize: "12px",
    fontWeight: "bold",
    backgroundColor: "#e8e8e8",
    borderBottom: "1px solid #999",
    padding: "3px 6px",
    marginTop: "16px",
    marginBottom: "8px",
    textTransform: "uppercase",
    pageBreakAfter: "avoid",
  },
  item: {
    fontSize: "11px",
    lineHeight: 1.45,
  },
  itemLabel: {
    fontWeight: "bold",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    marginTop: "4px",
    marginBottom: "6px",
    "& th, & td": {
      border: "1px solid #000",
      padding: "4px 7px",
      fontSize: "10px",
      fontFamily: "Times New Roman",
      textAlign: "left",
      verticalAlign: "middle",
    },
    "& th": {
      fontWeight: "bold",
      backgroundColor: "#ddd",
    },
    // Extra height on empty blank-template rows so there's room to write
    "& td$blankCell": {
      height: "26px",
    },
  },
  blankCell: {},
  notesBlock: {
    fontSize: "11px",
    whiteSpace: "pre-wrap",
    border: "1px solid #000",
    minHeight: "50px",
    padding: "6px",
    marginTop: "4px",
  },
  footerText: {
    fontSize: "8px",
    textAlign: "right",
    marginTop: "30px",
  },
  photoFigure: {
    breakInside: "avoid",
    margin: 0,
    marginBottom: "16px",
  },
  photoImage: {
    maxWidth: "100%",
    maxHeight: "430px",
    border: "1px solid #999",
  },
  photoCaption: {
    fontSize: "10px",
    fontStyle: "italic",
    marginTop: "2px",
  },
});

const Item = memo(({ label, children }) => {
  const classes = useStyles();
  return (
    <Typography component="div" className={classes.item}>
      <span className={classes.itemLabel}>{label}: </span>
      {children}
    </Typography>
  );
});

const Header = memo(() => {
  const classes = useStyles();
  return (
    <Grid container alignItems="center">
      <Grid item xs={4}>
        <img
          src="/static/img/pdf-print-logo.png"
          alt="BSEACD Logo"
          className={classes.headerImage}
        />
      </Grid>
      <Grid item xs={8} style={{ textAlign: "right" }}>
        <Typography className={classes.headerTitle}>
          Barton Springs/Edwards Aquifer Conservation District
        </Typography>
        <Typography className={classes.smallText}>
          1124 Regal Row Austin, TX 78748
        </Typography>
        <Typography className={classes.smallText}>(512) 282-8441</Typography>
      </Grid>
    </Grid>
  );
});

const BlankRows = ({ rows, cols }) => {
  const classes = useStyles();
  return Array.from({ length: rows }, (_, rowIdx) => (
    <tr key={rowIdx}>
      {Array.from({ length: cols }, (_, colIdx) => (
        <td key={colIdx} className={classes.blankCell}>
          &nbsp;
        </td>
      ))}
    </tr>
  ));
};

const WaterLevelsTable = memo(({ waterLevels, blank }) => {
  const classes = useStyles();
  return (
    <table className={classes.table}>
      <thead>
        <tr>
          <th>Time</th>
          <th>Method</th>
          <th>Measured From</th>
          <th>MP Height (in)</th>
          <th>DTW 1 (ft)</th>
          <th>DTW 2 (ft)</th>
          <th>DTW 3 (ft)</th>
          <th>Final DTW (ft)</th>
          <th>Pumping</th>
          <th>E-line Port</th>
          <th>Conductor Pipe</th>
        </tr>
      </thead>
      <tbody>
        {blank && <BlankRows rows={BLANK_WL_ROWS} cols={11} />}
        {waterLevels.map((wl) => (
          <tr key={wl.data_ndx}>
            <td>{fmtTime(wl.collect_timestamp)}</td>
            <td>{fmtText(wl.wl_meas_method_desc)}</td>
            <td>{fmtText(wl.wl_meas_from_desc)}</td>
            <td>{fmtNum(wl.wl_meas_point_height_in)}</td>
            <td>{fmtNum(wl.wl_meas_val_ft)}</td>
            <td>{fmtNum(wl.wl_meas_val_ft_2)}</td>
            <td>{fmtNum(wl.wl_meas_val_ft_3)}</td>
            <td>
              {fmtNum(finalDtw(wl.wl_meas_val_ft, wl.wl_meas_point_height_in))}
            </td>
            <td>{yn(wl.is_pumping)}</td>
            <td>{yn(wl.has_eline_access_port)}</td>
            <td>{yn(wl.has_conductor_pipe)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
});

const WqGridTable = memo(({ waterQuality }) => {
  const classes = useStyles();
  const { eventTimes, parameters } = buildWqGrid(waterQuality);
  return (
    <table className={classes.table}>
      <thead>
        <tr>
          <th>Parameter</th>
          {eventTimes.map((time) => (
            <th key={time}>{fmtTime(time)}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {parameters.map((param) => (
          <tr key={param.parameter_ndx}>
            <td>{param.label}</td>
            {param.results.map((result, idx) => (
              <td key={idx}>{result}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
});

const BlankWqGridTable = memo(() => {
  const classes = useStyles();
  return (
    <table className={classes.table}>
      <thead>
        <tr>
          <th>Parameter</th>
          {Array.from({ length: BLANK_WQ_READING_COLUMNS }, (_, idx) => (
            <th key={idx}>Reading {idx + 1}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {BLANK_WQ_PARAMETERS.map((param) => (
          <tr key={param}>
            <td>{param}</td>
            {Array.from({ length: BLANK_WQ_READING_COLUMNS }, (_, idx) => (
              <td key={idx} className={classes.blankCell}>
                &nbsp;
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
});

const SnapshotPrintContent = forwardRef(({ data, photos, blank }, ref) => {
  const classes = useStyles();
  const { inspection, waterLevels, waterQuality, meterReadings, documents } =
    data;
  const wqMeta = wqMetaSummary(waterQuality);
  const firstWl = waterLevels[0];

  // The paper form's "Time In" isn't captured by the app; approximate it with
  // the earliest same-day reading time when one exists.
  const timeIn = blank
    ? "______________"
    : [...waterLevels, ...waterQuality]
        .map((row) => row.collect_timestamp)
        .sort((a, b) => new Date(a) - new Date(b))[0];

  return (
    <div style={{ display: "none" }}>
      <div ref={ref}>
        <Paper className={classes.container} elevation={0}>
          <Header />
          <Typography className={classes.mainTitle}>
            BSEACD {reportTitle(inspection)}
          </Typography>

          <Typography className={classes.sectionTitle}>
            Inspection Details
          </Typography>
          <Grid container>
            <Grid item xs={4}>
              <Item label="Inspection Date">
                {fmtDateString(inspection.inspection_date)}
              </Item>
            </Grid>
            <Grid item xs={4}>
              <Item label="Time In (first reading)">
                {blank ? timeIn : timeIn ? fmtTime(timeIn) : "—"}
              </Item>
            </Grid>
            <Grid item xs={4}>
              <Item label="Inspection Type">
                {fmtText(inspection.ir_type_desc)}
              </Item>
            </Grid>
            <Grid item xs={4}>
              <Item label="Inspectors">{fmtText(inspection.inspected_by)}</Item>
            </Grid>
            <Grid item xs={4}>
              <Item label="District Well #">
                {fmtText(inspection.district_well_id)}
              </Item>
            </Grid>
            <Grid item xs={4}>
              <Item label="State Well #">
                {fmtText(inspection.state_well_num)}
              </Item>
            </Grid>
            <Grid item xs={4}>
              <Item label="Well Name">{fmtText(inspection.well_name)}</Item>
            </Grid>
            <Grid item xs={4}>
              <Item label="Classification">
                {[inspection.well_class_name, inspection.well_subclass_name]
                  .filter(Boolean)
                  .join(" / ") || "—"}
              </Item>
            </Grid>
            <Grid item xs={4}>
              <Item label="Primary Use">
                {fmtText(inspection.primary_use_desc)}
              </Item>
            </Grid>
            <Grid item xs={4}>
              <Item label="Well Status">
                {fmtText(inspection.well_status_desc)}
              </Item>
            </Grid>
          </Grid>

          <Typography className={classes.sectionTitle}>
            Permittee / Owner
          </Typography>
          <Grid container>
            <Grid item xs={6}>
              <Item label="Permittee / Owner">
                {fmtText(inspection.owner_primary_contact_name)}
              </Item>
            </Grid>
            <Grid item xs={6}>
              <Item label="Phone">
                {fmtText(inspection.owner_primary_contact_phone)}
              </Item>
            </Grid>
            <Grid item xs={6}>
              <Item label="Physical Address">
                {[inspection.physical_location_address, inspection.city]
                  .filter(Boolean)
                  .join(", ") || "—"}
              </Item>
            </Grid>
            <Grid item xs={6}>
              <Item label="GPS Coordinates of Well">
                {fmtGps(inspection.lat_dd, inspection.lon_dd)}
              </Item>
            </Grid>
          </Grid>

          <Typography className={classes.sectionTitle}>Site Checks</Typography>
          <Grid container>
            <Grid item xs={4}>
              <Item label="WQ Samples Collected">
                {blank ? "Yes / No" : waterQuality.length ? "Yes" : "No"}
              </Item>
            </Grid>
            <Grid item xs={4}>
              <Item label="Photos">
                {blank ? "Yes / No" : photos.length ? "Yes" : "No"}
              </Item>
            </Grid>
            <Grid item xs={4}>
              <Item label="E-line Access Port">
                {blank
                  ? "Yes / No"
                  : firstWl
                  ? yn(firstWl.has_eline_access_port)
                  : "—"}
              </Item>
            </Grid>
            <Grid item xs={4}>
              <Item label="Conductor Pipe">
                {blank
                  ? "Yes / No"
                  : firstWl
                  ? yn(firstWl.has_conductor_pipe)
                  : "—"}
              </Item>
            </Grid>
            <Grid item xs={4}>
              <Item label="Sampling Spigot">
                {fmtText(inspection.sampling_spigot_desc)}
              </Item>
            </Grid>
            <Grid item xs={4}>
              <Item label="Metered">{yn(inspection.is_metered)}</Item>
            </Grid>
          </Grid>

          <Typography className={classes.sectionTitle}>Meter</Typography>
          <Grid container>
            <Grid item xs={6}>
              <Item label="Meter Serial No">
                {fmtText(inspection.meter_sn)}
              </Item>
            </Grid>
            <Grid item xs={6}>
              <Item label="Meter Nickname">
                {fmtText(inspection.meter_nickname)}
              </Item>
            </Grid>
          </Grid>
          {(blank || meterReadings.length > 0) && (
            <table className={classes.table}>
              <thead>
                <tr>
                  <th>Meter</th>
                  <th>Month</th>
                  <th>Previous Reading</th>
                  <th>Reading</th>
                  <th>Calculated Gallons</th>
                </tr>
              </thead>
              <tbody>
                {blank && <BlankRows rows={BLANK_METER_ROWS} cols={5} />}
                {meterReadings.map((mr) => (
                  <tr key={mr.meter_reading_ndx}>
                    <td>{fmtText(mr.meter_id)}</td>
                    <td>
                      {mr.c_month}/{mr.c_year}
                    </td>
                    <td>{fmtNum(mr.prev_meter_reading)}</td>
                    <td>{fmtNum(mr.meter_reading)}</td>
                    <td>{fmtNum(mr.calculated_gallons)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <Typography className={classes.sectionTitle}>
            Surface Completion
          </Typography>
          <Grid container>
            <Grid item xs={4}>
              <Item label="Slab">{yn(inspection.is_slab)}</Item>
            </Grid>
            <Grid item xs={4}>
              <Item label="Slab Thickness">
                {fmtNum(inspection.slab_thickness_inches, " in")}
              </Item>
            </Grid>
            <Grid item xs={4}>
              <Item label="Slab Dimensions">
                {inspection.slab_length_inches || inspection.slab_width_inches
                  ? `${fmtNum(inspection.slab_length_inches)} x ${fmtNum(
                      inspection.slab_width_inches
                    )} in`
                  : "—"}
              </Item>
            </Grid>
            <Grid item xs={4}>
              <Item label="Steel Sleeve">{yn(inspection.is_steel_sleeve)}</Item>
            </Grid>
            <Grid item xs={4}>
              <Item label="Sleeve Height">
                {fmtNum(inspection.sleeve_height_inches, " in")}
              </Item>
            </Grid>
            <Grid item xs={4}>
              <Item label="Sleeve OD">
                {fmtNum(inspection.sleeve_outside_diameter_inches, " in")}
              </Item>
            </Grid>
            <Grid item xs={4}>
              <Item label="Casing Type">
                {fmtText(inspection.casing_type_desc)}
              </Item>
            </Grid>
            <Grid item xs={4}>
              <Item label="Casing OD">
                {fmtNum(inspection.casing_outside_diameter_inches, " in")}
              </Item>
            </Grid>
            <Grid item xs={4}>
              <Item label="Casing Stickup">
                {fmtNum(inspection.casing_height_inches, " in")}
              </Item>
            </Grid>
            <Grid item xs={12}>
              <Item label="Surface Completion Notes">
                {fmtText(inspection.surface_completion_notes)}
              </Item>
            </Grid>
          </Grid>

          <Typography className={classes.sectionTitle}>
            Equipment & Treatment
          </Typography>
          <Grid container>
            <Grid item xs={4}>
              <Item label="Storage Tank">{yn(inspection.storage_tank)}</Item>
            </Grid>
            <Grid item xs={4}>
              <Item label="Disinfection/Treatment">
                {fmtText(inspection.other_treatments_desc)}
              </Item>
            </Grid>
            <Grid item xs={4}>
              <Item label="Treatment Notes">
                {fmtText(inspection.treatments_notes)}
              </Item>
            </Grid>
            <Grid item xs={4}>
              <Item label="Flow Rate">
                {fmtNum(inspection.flow_rate_gpm, " gpm")}
              </Item>
            </Grid>
            <Grid item xs={4}>
              <Item label="Pump Size">
                {fmtNum(inspection.pump_motor_size_hp, " hP")}
              </Item>
            </Grid>
            <Grid item xs={4}>
              <Item label="Pump Type">
                {fmtText(inspection.pump_type_desc)}
              </Item>
            </Grid>
            <Grid item xs={4}>
              <Item label="System Type">
                {fmtText(inspection.system_type_desc)}
              </Item>
            </Grid>
            <Grid item xs={4}>
              <Item label="Pump Depth">
                {fmtNum(inspection.pump_depth_ft, " ft")}
              </Item>
            </Grid>
            <Grid item xs={4}>
              <Item label="Total Borehole Depth">
                {fmtNum(inspection.total_borehole_depth_ft, " ft")}
              </Item>
            </Grid>
          </Grid>

          <Typography className={classes.sectionTitle}>
            Well Location Setbacks
          </Typography>
          <table className={classes.table}>
            <thead>
              <tr>
                <th>Well Location</th>
                <th>Required Distance (ft)</th>
                <th>Actual</th>
              </tr>
            </thead>
            <tbody>
              {setbackRows(inspection).map((row) => (
                <tr key={row.label}>
                  <td>{row.label}</td>
                  <td>{row.required}</td>
                  <td>{row.actual}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {inspection.setback_notes && (
            <Item label="Setback Notes">{inspection.setback_notes}</Item>
          )}

          <Typography className={classes.sectionTitle}>
            Inspection Checklist
          </Typography>
          <Grid container>
            {INSPECTION_CHECKLIST.map(({ field, label }) => (
              <Grid item xs={4} key={field}>
                <Typography component="div" className={classes.item}>
                  {inspection[field] ? "☑" : "☐"} {label}
                </Typography>
              </Grid>
            ))}
          </Grid>

          <Typography className={classes.sectionTitle}>Water Levels</Typography>
          {blank ? (
            <WaterLevelsTable waterLevels={[]} blank />
          ) : waterLevels.length ? (
            <>
              <WaterLevelsTable waterLevels={waterLevels} />
              {waterLevels.some((wl) => wl.remarks || wl.sampled_by) && (
                <Item label="WL Remarks">
                  {waterLevels
                    .map((wl) =>
                      [wl.sampled_by, wl.remarks].filter(Boolean).join(": ")
                    )
                    .filter(Boolean)
                    .join("; ") || "—"}
                </Item>
              )}
            </>
          ) : (
            <Typography className={classes.item}>
              No water level measurements recorded during this visit.
            </Typography>
          )}

          <Typography className={classes.sectionTitle}>
            Water Quality Parameters
          </Typography>
          {blank ? (
            <>
              <Typography className={classes.item}>
                At least 3 readings at 5 min intervals — this purges the well
                prior to sample collection.
              </Typography>
              <BlankWqGridTable />
            </>
          ) : waterQuality.length ? (
            <>
              <WqGridTable waterQuality={waterQuality} />
              {wqMeta && (
                <Grid container>
                  <Grid item xs={3}>
                    <Item label="Well Purged">{wqMeta.purged}</Item>
                  </Grid>
                  <Grid item xs={3}>
                    <Item label="Lab">{wqMeta.lab}</Item>
                  </Grid>
                  <Grid item xs={3}>
                    <Item label="Equipment">{wqMeta.equipment}</Item>
                  </Grid>
                  <Grid item xs={3}>
                    <Item label="Sampled By">{wqMeta.sampledBy}</Item>
                  </Grid>
                  {wqMeta.notes && (
                    <Grid item xs={12}>
                      <Item label="WQ Notes">{wqMeta.notes}</Item>
                    </Grid>
                  )}
                </Grid>
              )}
            </>
          ) : (
            <Typography className={classes.item}>
              No water quality samples recorded during this visit.
            </Typography>
          )}

          <Typography className={classes.sectionTitle}>
            Other Notes or Well Construction Issues
          </Typography>
          <Typography component="div" className={classes.notesBlock}>
            {inspection.field_notes || ""}
          </Typography>

          {documents.length > 0 && (
            <>
              <Typography className={classes.sectionTitle}>
                Documents On File For This Visit
              </Typography>
              {documents.map((doc) => (
                <Typography key={doc.att_ndx} className={classes.item}>
                  • {doc.original_filename}
                  {doc.notes ? ` — ${doc.notes}` : ""}
                </Typography>
              ))}
            </>
          )}

          <Typography className={classes.footerText}>
            {blank ? (
              <>
                Blank field sheet template generated{" "}
                {new Date().toLocaleDateString("en-US")} from the BSEACD Field
                Services system.
              </>
            ) : (
              <>
                Generated from the BSEACD Field Services database on{" "}
                {new Date().toLocaleDateString("en-US")}. Record created{" "}
                {fmtTimestamp(inspection.created_timestamp)}. Water level, water
                quality, and photo data include all entries recorded for this
                well on the inspection date; well details reflect the current
                well record.
              </>
            )}
          </Typography>
        </Paper>

        {photos.length > 0 && (
          <Paper className={classes.container} elevation={0}>
            <Header />
            <Typography className={classes.mainTitle}>
              Inspection Photos — {fmtText(inspection.district_well_id)} —{" "}
              {fmtDateString(inspection.inspection_date)}
            </Typography>
            {photos.map((photo) => (
              <figure key={photo.att_ndx} className={classes.photoFigure}>
                <img
                  src={photo.objectUrl}
                  alt={photo.original_filename}
                  className={classes.photoImage}
                />
                <figcaption className={classes.photoCaption}>
                  {[
                    photo.original_filename,
                    photo.photo_taken_by
                      ? `Taken by ${photo.photo_taken_by}`
                      : null,
                    photo.notes,
                  ]
                    .filter(Boolean)
                    .join(" — ")}
                </figcaption>
              </figure>
            ))}
          </Paper>
        )}
      </div>
    </div>
  );
});

export default memo(SnapshotPrintContent);
